import React, { useState, useEffect, useMemo } from 'react';
import './SubscriptionPage.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import { loadStripe } from '@stripe/stripe-js';
import { create_subscription, get_billing_status, create_setup_intent_for_update } from '../Services/Student/PaymentApi';
import ErrorModel from './ErrorModel';
import { Elements } from '@stripe/react-stripe-js';
import SuccessModel from './SuccessModel';
import StripeOffcanvas from './StripeOffcanvas';
import ConfirmationModal from './ConfirmationModal';

// Load Stripe outside of a component’s render to avoid
// recreating the Stripe object on every render.
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) : null;

// --- Debugging Environment Variable ---
// This will log the Stripe key to your browser's console to verify it's loaded.
console.log('Stripe Publishable Key:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// --- Centralized Plan Configuration ---
// This structure groups all plan data, including environment-specific Stripe Price IDs.
// This makes it easier to manage and aligns with the backend's expectations.
const PLANS_CONFIG = {
  premium: {
    name: 'PREMIUM',
    monthlyPrice: 9.99,
    annualPrice: 83.99,
    priceIds: {
      development: {
        monthly: 'PREMIUM_MONTHLY',
        annually: 'PREMIUM_ANNUALLY',
      },
      production: {
        monthly: 'PREMIUM_MONTHLY',
        annually: 'PREMIUM_ANNUALLY',
      },
    },
  },
  elite: {
    name: 'ELITE',
    monthlyPrice: 19.99,
    annualPrice: 167.99,
    priceIds: {
      development: {
        monthly: 'ELITE_MONTHLY',
        annually: 'ELITE_ANNUALLY',
      },
      production: {
        monthly: 'ELITE_MONTHLY',
        annually: 'ELITE_ANNUALLY',
      },
    },
  },
};

const SubscriptionPage = () => {
  const [billingCycle, setBillingCycle] = useState('monthly'); // Default, will be updated by useEffect
  const [isOffcanvasOpen, setOffcanvasOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenErrorModel, setOpenErrorModel] = useState(false);
  const [paymentMode, setPaymentMode] = useState('payment'); // 'payment' or 'setup'
  const [errorMessage, setErrorMessage] = useState('');
  const [amountDue, setAmountDue] = useState(null);
  // --- Development Only: Use setup mode to test UI without a backend ---
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [activeSubscription, setActiveSubscription] = useState({ tier: null, cycle: null });
  const [isDowngradeConfirmOpen, setIsDowngradeConfirmOpen] = useState(false);
  const [pendingDowngrade, setPendingDowngrade] = useState(null);
  const [showStripeConfigErrorModal, setShowStripeConfigErrorModal] = useState(true); // New state to control this specific error modal
  // --- Feature Flag for Subscription Model ---
  // Set to true to use the new `create_subscription` flow.
  const useSubscriptionModel = true;
  const [isSetupMode, setIsSetupMode] = useState(false); // Set to false to enable backend integration

  const navigate = useNavigate();
  const location = useLocation();

  const cheapestAnnualPrice = Math.min(PLANS_CONFIG.premium.annualPrice, PLANS_CONFIG.elite.annualPrice);
  const dailyPrice = Math.ceil(cheapestAnnualPrice / 365 / 0.05) * 0.05;

  const getPlanDetails = (planType, cycle = billingCycle) => {
    const planConfig = PLANS_CONFIG[planType];
    if (!planConfig) return null;

    return {
      name: planConfig.name,
      price: cycle === 'annually' ? planConfig.annualPrice : planConfig.monthlyPrice,
      cycle,
    };
  };

  const getPlanTypeByName = planName => Object.entries(PLANS_CONFIG).find(([, planConfig]) => planConfig.name === planName)?.[0] || null;

  // Effect to handle pre-selection from navigation state
  useEffect(() => {
    const preSelectedPlanType = location.state?.preSelectedPlan; // 'premium' or 'elite'
    if (preSelectedPlanType && PLANS_CONFIG[preSelectedPlanType]) {
      const planDetails = getPlanDetails(preSelectedPlanType);
      setSelectedPlan(planDetails);
    }
  }, [location.state]); // Runs only when navigation state changes

  // This function can now be called from anywhere inside the component.
  const fetchStatus = async () => {
    try {
      const data = await get_billing_status();
      if (data && data.plan && !data.code?.includes('ERR')) {
        // The backend returns 'month' or 'annual'. We map it to 'monthly' or 'annually'.
        const cycle = data.plan.period === 'annual' ? 'annually' : 'monthly';
        setActiveSubscription({ tier: data.plan.tier, cycle: cycle });
        // Initialize billingCycle to match the active subscription's cycle
        // Only update if the user is on a paid plan to avoid switching FREE users to monthly default
        if (data.plan.tier !== 'FREE') {
          setBillingCycle(cycle);
        }
      } else {
        setBillingCycle('monthly'); // Default to monthly if no active plan or error
        setActiveSubscription({ tier: null, cycle: null }); // Reset on error
      }
    } catch (error) {
      console.error('Error fetching billing status:', error);
      setActiveSubscription({ tier: null, cycle: null });
    }
  };

  // Fetch billing status once on component mount
  useEffect(() => {
    fetchStatus();
  }, []); // Empty dependency array ensures this runs only once

  // Keep the selected tier when the billing cycle changes, but refresh its price/cycle.
  useEffect(() => {
    setSelectedPlan(prevPlan => {
      const selectedPlanType = getPlanTypeByName(prevPlan?.name);
      return selectedPlanType ? getPlanDetails(selectedPlanType, billingCycle) : null;
    });

    setOffcanvasOpen(false); // Also close the offcanvas if it's open
  }, [billingCycle]);

  const calculateDiscount = (monthlyPrice, annualPrice) => {
    const totalMonthlyCost = monthlyPrice * 12;
    return Math.round(((totalMonthlyCost - annualPrice) / totalMonthlyCost) * 100);
  };

  const getPlanRank = tier => {
    if (tier === 'ELITE') return 2;
    if (tier === 'PREMIUM') return 1;
    return 0; // FREE or null
  };

  const handleUpgradeClick = planType => {
    const currentRank = getPlanRank(activeSubscription.tier);
    const newRank = getPlanRank(PLANS_CONFIG[planType].name);
    const isDowngrade = newRank < currentRank;

    const planDetails = {
      name: PLANS_CONFIG[planType].name,
      price: billingCycle === 'annually' ? PLANS_CONFIG[planType].annualPrice : PLANS_CONFIG[planType].monthlyPrice,
      cycle: billingCycle, // 'annually' or 'monthly'
    };
    setSelectedPlan(planDetails);

    if (isDowngrade) {
      setPendingDowngrade(planType);
      setIsDowngradeConfirmOpen(true);
    } else {
      // It's an upgrade or same-level change (e.g., monthly to annual)
      if (isSetupMode) {
        // In UI test mode, we don't need to call an API.
        // We'll just open the offcanvas.
        // --- In UI Test Mode ---
        // We don't need a clientSecret. We'll just open the offcanvas.
        setOffcanvasOpen(true);
      } else {
        initiatePayment(planType);
      }
    }
  };

  const handleConfirmDowngrade = () => {
    setIsDowngradeConfirmOpen(false);
    if (pendingDowngrade) {
      const planType = pendingDowngrade;
      setPendingDowngrade(null);
      // It's an upgrade or same-level change (e.g., monthly to annual)
      if (isSetupMode) {
        setOffcanvasOpen(true);
      }
      initiatePayment(planType);
    }
  };

  const initiatePayment = async planType => {
    // --- In Live/Integration Mode ---
    if (useSubscriptionModel) {
      // New Subscription Flow
      setIsLoading(true);
      try {
        // Select the correct priceId based on environment and billing cycle
        const env = process.env.NODE_ENV === 'production' ? 'production' : 'development';
        const priceId = PLANS_CONFIG[planType].priceIds[env][billingCycle];

        const response = await create_subscription({ priceId });

        // The API can return a clientSecret (for PaymentIntents) or a setupSecret (for SetupIntents).
        const paymentIntentSecret = response?.clientSecret;
        const setupIntentSecret = response?.setupSecret;

        // If amountDue is returned, update the selected plan's price
        if (response.amountDue !== undefined) {
          setAmountDue(response.amountDue);
          setSelectedPlan(prevPlan => ({
            ...prevPlan,
            price: response.amountDue / 100, // Convert cents to dollars
          }));
        }

        if (paymentIntentSecret) {
          setClientSecret(paymentIntentSecret);
          setPaymentMode(response.flow || 'payment'); // Use flow from API, default to 'payment'
          setOffcanvasOpen(true);
        } else if (setupIntentSecret) {
          // This block handles the "setup" flow.
          // The backend returns a `setupSecret` when it needs to save a payment method
          // for a subscription that doesn't require immediate payment.
          // We place this `setupSecret` into our `clientSecret` state variable,
          // as the Stripe Elements provider expects it there.
          setClientSecret(setupIntentSecret);
          // We MUST set the mode to 'setup' so the payment form knows to confirm a SetupIntent
          // instead of a PaymentIntent.
          setPaymentMode('setup'); // A setupSecret always implies 'setup' mode.
          setOffcanvasOpen(true);
        } else if (response.flow === 'none') {
          // This handles successful subscription changes that don't require payment,
          // such as downgrades or applying a trial.
          setSuccessMessage('Your subscription has been updated successfully!');
          setIsSuccessOpen(true);
          // Optionally, you could trigger a refetch of billing status here.
        } else {
          // This handles cases where the API call was successful (e.g., 200 OK)
          // but the response body did not contain the expected secret.
          const errorMsg = response?.response?.data?.error?.message || 'Could not retrieve payment details from the server.';
          console.error('API Error: Missing clientSecret or setupSecret in response.', response);
          setErrorMessage(errorMsg);
          setOpenErrorModel(true);
        }
      } catch (error) {
        console.error('Error creating subscription:', error);
        setErrorMessage('An unexpected error occurred while setting up your subscription. Please try again later.');
        setOpenErrorModel(true);
      }
    } else {
      // Old Payment Intent Flow
      try {
        const data = await create_payment_intent({
          planId: `${planType.toUpperCase()}_${billingCycle.toUpperCase()}`,
        });

        if (data?.clientSecret) {
          setClientSecret(data?.clientSecret);
          setOffcanvasOpen(true);
        } else {
          console.error('Error: clientSecret not received from create_payment_intent.', data);
        }
      } catch (error) {
        console.error('Error creating PaymentIntent:', error);
      }
    }
    setIsLoading(false);
  };

  const handleChangePaymentMethod = async () => {
    setIsLoading(true);
    try {
      const response = await create_setup_intent_for_update();
      if (response?.clientSecret) {
        setClientSecret(response.clientSecret);
        setPaymentMode('setup');
        setSelectedPlan({
          name: 'Update Payment Method',
          price: 0, // No immediate charge
          cycle: '',
        });
        setOffcanvasOpen(true);
      } else {
        setErrorMessage(response?.message || 'Could not retrieve payment details. Please try again.');
        setOpenErrorModel(true);
      }
    } catch (error) {
      setErrorMessage('An error occurred while preparing to update your payment method.');
      setOpenErrorModel(true);
    } finally {
      setIsLoading(false);
    }
  };
  const handleCloseOffcanvas = async () => {
    await fetchStatus(); // Re-fetch billing status when the payment window is closed
    setOffcanvasOpen(false);
    setClientSecret(''); // Clear the secret when closing
    setPaymentMode('payment'); // Reset to default mode
  };

  // Memoize the options object to prevent re-creating it on every render.
  // This is crucial for the Elements provider to work correctly.
  const options = useMemo(() => {
    if (paymentMode === 'setup') {
      return {
        mode: 'setup',
        currency: 'usd', // currency is required for setup mode
        clientSecret: clientSecret,
        appearance: { theme: 'stripe' },
      };
    }
    // For 'payment' mode, only clientSecret is needed.
    return { clientSecret, appearance: { theme: 'stripe' } };
  }, [clientSecret, paymentMode]);

  // --- Stripe Initialization Check ---
  // If the stripePromise is null, it means the environment variable is missing.
  // We render an error message instead of the payment flow, but allow it to be dismissed.
  if (!stripePromise && showStripeConfigErrorModal) {
    return (
      <div className="subscription-page-container">
        <ErrorModel
          isOpen={true}
          title="Configuration Error"
          message="Stripe is not configured correctly. Please check your environment variables."
          buttonText="Close"
          onClose={() => setShowStripeConfigErrorModal(false)}
        />
      </div>
    );
  }
  // The main component now returns the Elements provider, which wraps the page content.
  // This ensures Stripe is initialized once and persists.
  return (
    <div className="subscription-page-container">
      {clientSecret && stripePromise && isOffcanvasOpen && (
        <Elements stripe={stripePromise} options={options} key={clientSecret}>
          <StripeOffcanvas isOpen={isOffcanvasOpen} onClose={handleCloseOffcanvas} plan={selectedPlan} mode={paymentMode} />
        </Elements>
      )}
      <div className="subscription-content-centered">
        {/* Left Side - Subscription Plans */}
        <div className="subscription-header-section">
          <h2>{`BOOST your career and job hunt NOW at less than $${dailyPrice.toFixed(2)}/day`}</h2>

          <div className="billing-toggle">
            <button
              className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
              onClick={() => !isOffcanvasOpen && setBillingCycle('monthly')}
              disabled={isOffcanvasOpen}
            >
              Monthly
            </button>
            <button
              className={`toggle-btn ${billingCycle === 'annually' ? 'active' : ''}`}
              onClick={() => !isOffcanvasOpen && setBillingCycle('annually')}
              disabled={isOffcanvasOpen}
            >
              Annually
              <span className="recommended-badge">Recommended</span>
            </button>
          </div>
        </div>

        <div className="subscription-plans-wrapper">
          {/* FREE Plan */}
          <div className="plan-card free-plan" style={{ cursor: 'default' }}>
            <div className="plan-header">
              <h3>
                FREE
                {activeSubscription.tier === 'FREE' && <FaCheckCircle className="active-checkmark" />}
              </h3>
              <div className="price-container">
                <span className="price">$0.00</span>
                <span className="billing-period">/ forever</span>
              </div>
              <p className="billing-description">Always free</p>
            </div>
            <div className="plan-body">
              <p className="plan-description">Essential tools to get you started on your career journey.</p>
              <ul className="features-list">
                <li>
                  <FaCheckCircle className="check-icon" /> Up to 3 Enhanced Resumes each day
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Up to 3 Cover Letter generations each day
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Up to 3 Emails to connect with insiders on the Insider Network each day
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Limited Access to Seeky Assistant for interview prep
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Limited Access to personalized skill-gap analysis
                </li>
              </ul>
            </div>
            <button
              className={`upgrade-btn ${activeSubscription.tier === 'FREE' ? 'currently-active' : ''}`}
              disabled={true}
              style={{ cursor: 'default', opacity: activeSubscription.tier === 'FREE' ? 1 : 0.6 }}
            >
              {activeSubscription.tier === 'FREE' ? 'Currently Active' : 'Basic Plan'}
            </button>
          </div>

          {/* PREMIUM Plan */}
          <div
            className={`plan-card ${selectedPlan?.name === 'PREMIUM' ? 'selected' : ''}`}
            onClick={() => {
              if (!isOffcanvasOpen) {
                setSelectedPlan({
                  name: PLANS_CONFIG.premium.name,
                  price: billingCycle === 'annually' ? PLANS_CONFIG.premium.annualPrice : PLANS_CONFIG.premium.monthlyPrice,
                  cycle: billingCycle,
                });
              }
            }}
            style={{ opacity: isOffcanvasOpen ? 0.6 : 1, cursor: isOffcanvasOpen ? 'not-allowed' : 'pointer' }}
          >
            <div className="plan-header">
              <h3>
                PREMIUM
                {activeSubscription.tier === 'PREMIUM' && <FaCheckCircle className="active-checkmark" />}
              </h3>
              <div className="price-container">
                {billingCycle === 'annually' ? (
                  <>
                    <span className="old-price">${(PLANS_CONFIG.premium.monthlyPrice * 12).toFixed(2)}</span>
                    <span className="price">${PLANS_CONFIG.premium.annualPrice}</span>
                    <span className="discount-badge">
                      Save {calculateDiscount(PLANS_CONFIG.premium.monthlyPrice, PLANS_CONFIG.premium.annualPrice)}%
                    </span>
                  </>
                ) : (
                  <span className="price">${PLANS_CONFIG.premium.monthlyPrice}</span>
                )}
                <span className="billing-period">/ {billingCycle === 'annually' ? 'year' : 'month'}</span>
              </div>
              <p className="billing-description">{billingCycle === 'annually' ? 'Billed annually' : 'Billed monthly'}</p>
            </div>
            <div className="plan-body">
              <p className="plan-description">
                Maximize your job hunt with expanded daily access to resumes, cover letters and insider outreach.
              </p>
              <ul className="features-list">
                <li>
                  <FaCheckCircle className="check-icon" /> Up to 20 Enhanced Resumes each day
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Up to 20 Cover Letter generations each day
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Up to 20 Emails to connect with insiders on the Insider Network each day
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Increased Access to Seeky Assistant for interview prep
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Increased Access to personalized skill-gap analysis
                </li>
              </ul>
            </div>
            <button
              className={`upgrade-btn ${
                activeSubscription.tier === 'PREMIUM' && activeSubscription.cycle === billingCycle ? 'currently-active' : ''
              }`}
              onClick={e => {
                e.stopPropagation();
                handleUpgradeClick('premium');
              }}
              disabled={
                isLoading || isOffcanvasOpen || (activeSubscription.tier === 'PREMIUM' && activeSubscription.cycle === billingCycle)
              }
            >
              {activeSubscription.tier === 'PREMIUM' && activeSubscription.cycle === billingCycle
                ? 'Currently Active'
                : isLoading && selectedPlan?.name === 'PREMIUM'
                  ? 'Processing...'
                  : 'Change Plan'}
            </button>
          </div>

          {/* ELITE Plan */}
          <div
            className={`plan-card elite ${selectedPlan?.name === 'ELITE' ? 'selected' : ''}`}
            data-ribbon-text="Best Value"
            onClick={() => {
              if (!isOffcanvasOpen) {
                setSelectedPlan({
                  name: PLANS_CONFIG.elite.name,
                  price: billingCycle === 'annually' ? PLANS_CONFIG.elite.annualPrice : PLANS_CONFIG.elite.monthlyPrice,
                  cycle: billingCycle,
                });
              }
            }}
            style={{ opacity: isOffcanvasOpen ? 0.6 : 1, cursor: isOffcanvasOpen ? 'not-allowed' : 'pointer' }}
          >
            <div className="plan-header">
              <h3>
                ELITE
                {activeSubscription.tier === 'ELITE' && <FaCheckCircle className="active-checkmark" />}
              </h3>
              <div className="price-container">
                {billingCycle === 'annually' ? (
                  <>
                    <span className="old-price">${(PLANS_CONFIG.elite.monthlyPrice * 12).toFixed(2)}</span>
                    <span className="price">${PLANS_CONFIG.elite.annualPrice}</span>
                    <span className="discount-badge">
                      Save {calculateDiscount(PLANS_CONFIG.elite.monthlyPrice, PLANS_CONFIG.elite.annualPrice)}%
                    </span>
                  </>
                ) : (
                  <span className="price">${PLANS_CONFIG.elite.monthlyPrice}</span>
                )}
                <span className="billing-period">/ {billingCycle === 'annually' ? 'year' : 'month'}</span>
              </div>
              <p className="billing-description">{billingCycle === 'annually' ? 'Billed annually' : 'Billed monthly'}</p>
            </div>
            <div className="plan-body">
              <p className="plan-description">
                Supercharge your job hunt with 50 daily resumes, cover letters, and insider emails, plus dedicated career support.
              </p>
              <ul className="features-list">
                <li>
                  <FaCheckCircle className="check-icon" />
                  Up to 50 Enhanced Resumes each day
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Up to 50 Cover Letter generations each day
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Up to 50 Emails to connect with insiders on the Insider Network each day
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Full Access to Seeky Assistant for interview prep
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Full Access to personalized skill-gap analysis
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Priority access to new features
                </li>
                <li>
                  <FaCheckCircle className="check-icon" /> Dedicated career support
                </li>
              </ul>
            </div>
            <button
              className={`upgrade-btn ${
                activeSubscription.tier === 'ELITE' && activeSubscription.cycle === billingCycle ? 'currently-active' : ''
              }`}
              onClick={e => {
                e.stopPropagation();
                handleUpgradeClick('elite');
              }}
              disabled={isLoading || isOffcanvasOpen || (activeSubscription.tier === 'ELITE' && activeSubscription.cycle === billingCycle)}
            >
              {activeSubscription.tier === 'ELITE' && activeSubscription.cycle === billingCycle
                ? 'Currently Active'
                : isLoading && selectedPlan?.name === 'ELITE'
                  ? 'Processing...'
                  : 'Change Plan'}
            </button>
          </div>
          {/* {activeSubscription.tier && activeSubscription.tier !== 'FREE' && (
            <div className="text-center mt-4">
              <p className="mb-1">Want to use a different card for your subscription?</p>
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  handleChangePaymentMethod();
                }}
                className="change-payment-link"
              >
                Change Payment Method
              </a>
            </div>
          )} */}
        </div>
        <footer className="payment-security-footer" aria-label="Payment security information">
          <h4>Secure Payments</h4>
          <p>
            SeekJobs.ai uses Stripe's secure payment processing with industry-standard encryption. Your card details are never stored on our
            servers.
          </p>
        </footer>
      </div>
      {isOpenErrorModel && (
        <ErrorModel
          isOpen={isOpenErrorModel}
          title="Alert!"
          message={errorMessage}
          buttonText="OK"
          onClose={() => setOpenErrorModel(false)}
        />
      )}
      {isSuccessOpen && (
        <SuccessModel
          isOpen={isSuccessOpen}
          title="Success!"
          message={successMessage}
          buttonText="OK"
          onClose={() => {
            setIsSuccessOpen(false);
            fetchStatus(); // Re-fetch the billing status to update the UI
            navigate('/seeker-dashboard');
          }}
        />
      )}
      {isDowngradeConfirmOpen && (
        <ConfirmationModal
          isOpen={isDowngradeConfirmOpen}
          title="Confirm Downgrade"
          message="Your new plan will become effective at the end of your current billing cycle."
          subMessage="Are you sure you want to proceed?"
          confirmText="Yes, Downgrade"
          cancelText="Cancel"
          action="Confirm" // A neutral action type
          onConfirm={handleConfirmDowngrade}
          onCancel={() => {
            setIsDowngradeConfirmOpen(false);
            setPendingDowngrade(null);
          }}
        />
      )}
    </div>
  );
};

export default SubscriptionPage;
